/**
 * Production Budget Monitoring Utility Functions
 * 
 * Helper functions for Production Budget Monitoring module
 */

import {
    ProductionBudgetDataRaw,
    ProductionBudgetRow,
    ProductionBudgetHierarchy,
    ProductionBudgetGrouped
} from './types';

/**
 * Format number with 2 decimal places
 * Returns '-' for null, undefined, NaN, Infinity, or zero values
 */
export const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    if (!Number.isFinite(value)) return '-';
    if (value === 0) return '-';
    return value.toLocaleString('id-ID', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * Calculate variance percentage
 * Variance = ((Actual - Budget) / Budget) * 100
 * If budget is 0 and actual is 0, variance is 0
 * If budget is 0 and actual > 0, variance is null (or 100% over)
 */
export const calculateVariance = (actual: number, budget: number): number | null => {
    if (budget === 0 && actual === 0) return 0;
    if (budget === 0) return null;
    return ((actual - budget) / budget) * 100;
};

/**
 * Format variance percentage for display
 * Returns '-' for null, Infinity, or zero variance
 */
export const formatVariance = (variance: number | null): string => {
    if (variance === null) return '-';
    if (!Number.isFinite(variance)) return '-';
    if (variance === 0) return '-';
    const sign = variance > 0 ? '+' : '';
    return `${sign}${variance.toFixed(2)}%`;
};

/**
 * Safe division helper
 */
const safeDivide = (numerator: number, denominator: number): number | null => {
    if (denominator === 0) return null;
    return numerator / denominator;
};

/**
 * Extract activity code from activity string (e.g., "8108103 - UPAH PANEN" → "8108103")
 */
const getActivityCode = (activity: string): string => {
    return activity.split(' - ')[0] || '';
};

/**
 * Special parameter configuration for activity code 8108103 (UPAH PANEN)
 * Format: [parameter_name, actual_formula, has_budget]
 */
interface SpecialParameter {
    parameter: string;
    getActualMtd: (item: ProductionBudgetDataRaw) => number | null;
    getBudgetMtd: (item: ProductionBudgetDataRaw) => number | null;
    getActualYtd: (item: ProductionBudgetDataRaw) => number | null;
    getBudgetYtd: (item: ProductionBudgetDataRaw) => number | null;
    hasBudget: boolean;
}

/**
 * Get special parameters for activity code 8108103 (UPAH PANEN)
 */
const getSpecialParameters = (item: ProductionBudgetDataRaw, budgetMonth: number): SpecialParameter[] => {
    return [
        {
            parameter: 'Produksi TBS (kg)',
            getActualMtd: (i) => i.weight_act_mtd,
            getBudgetMtd: (i) => i.weight_bgt_mtd,
            getActualYtd: (i) => i.weight_act_ytd,
            getBudgetYtd: (i) => i.weight_bgt_ytd,
            hasBudget: true,
        },
        {
            parameter: 'Luas Panen (Ha)',
            getActualMtd: (i) => i.harvest_area_mtd,
            getBudgetMtd: () => null,
            getActualYtd: (i) => i.harvest_area_ytd,
            getBudgetYtd: () => null,
            hasBudget: false,
        },
        {
            parameter: 'Rotasi',
            getActualMtd: (i) => safeDivide(i.harvest_area_mtd, i.planted_area),
            getBudgetMtd: () => null,
            getActualYtd: (i) => {
                // const plantedAreaPerMonth = safeDivide(i.planted_area, budgetMonth);
                const harvestAreaYtd = safeDivide(i.harvest_area_ytd, i.planted_area);
                if (harvestAreaYtd === null) return null;
                return safeDivide(harvestAreaYtd, budgetMonth);
            },
            getBudgetYtd: () => null,
            hasBudget: false,
        },
        {
            parameter: 'HK',
            getActualMtd: (i) => i.hk_act_mtd,
            getBudgetMtd: (i) => i.hk_bgt_mtd,
            getActualYtd: (i) => i.hk_act_ytd,
            getBudgetYtd: (i) => i.hk_bgt_ytd,
            hasBudget: true,
        },
        {
            parameter: 'JJG',
            getActualMtd: (i) => i.jjg_act_mtd,
            getBudgetMtd: (i) => i.jjg_bgt_mtd,
            getActualYtd: (i) => i.jjg_act_ytd,
            getBudgetYtd: (i) => i.jjg_bgt_ytd,
            hasBudget: true,
        },
        {
            parameter: 'Kg Basis',
            getActualMtd: (i) => i.weight_act_mtd - i.lebih_basis_act_mtd - i.brd_qty_act_mtd,
            getBudgetMtd: () => null,
            getActualYtd: (i) => i.weight_act_ytd - i.lebih_basis_act_ytd - i.brd_qty_act_ytd,
            getBudgetYtd: () => null,
            hasBudget: false,
        },
        {
            parameter: 'Kg > Basis',
            getActualMtd: (i) => i.lebih_basis_act_mtd,
            getBudgetMtd: () => null,
            getActualYtd: (i) => i.lebih_basis_act_ytd,
            getBudgetYtd: () => null,
            hasBudget: false,
        },
        {
            parameter: 'Kg Brondolan',
            getActualMtd: (i) => i.brd_qty_act_mtd,
            getBudgetMtd: () => null,
            getActualYtd: (i) => i.brd_qty_act_ytd,
            getBudgetYtd: () => null,
            hasBudget: false,
        },
        {
            parameter: 'BJR',
            getActualMtd: (i) => safeDivide(i.weight_act_mtd, i.jjg_act_mtd),
            getBudgetMtd: (i) => safeDivide(i.weight_bgt_mtd, i.jjg_bgt_mtd),
            getActualYtd: (i) => safeDivide(i.weight_act_ytd, i.jjg_act_ytd),
            getBudgetYtd: (i) => safeDivide(i.weight_bgt_ytd, i.jjg_bgt_ytd),
            hasBudget: true,
        },
        {
            parameter: 'Kg/HK',
            getActualMtd: (i) => safeDivide(i.weight_act_mtd, i.hk_act_mtd),
            getBudgetMtd: (i) => safeDivide(i.weight_bgt_mtd, i.hk_bgt_mtd),
            getActualYtd: (i) => safeDivide(i.weight_act_ytd, i.hk_act_ytd),
            getBudgetYtd: (i) => safeDivide(i.weight_bgt_ytd, i.hk_bgt_ytd),
            hasBudget: true,
        },
        {
            parameter: 'Yield (Ton/Ha)',
            getActualMtd: (i) => safeDivide(i.weight_act_mtd/1000, i.planted_area),
            getBudgetMtd: (i) => safeDivide(i.weight_bgt_mtd/1000, i.planted_area),
            getActualYtd: (i) => safeDivide(i.weight_act_ytd/1000, i.planted_area),
            getBudgetYtd: (i) => safeDivide(i.weight_bgt_ytd/1000, i.planted_area),
            hasBudget: true,
        },
    ];
};

/**
 * Transform raw API data to display rows
 * Special handling for activity code 8108103 (UPAH PANEN)
 * Standard handling for all other activities (like upkeep monitoring)
 */
export const transformToDisplayRows = (
    productionData: ProductionBudgetDataRaw[],
    budgetMonth: string
): ProductionBudgetRow[] => {
    const rows: ProductionBudgetRow[] = [];
    const month = Number.parseInt(budgetMonth, 10);

    productionData.forEach((item, index) => {
        const { coa, coa_group, activity, uom, cost_activity_id } = item;
        const activityCode = getActivityCode(activity);

        // Special handling for activity code 8108103 (UPAH PANEN)
        if (activityCode === '8108103') {
            const specialParams = getSpecialParameters(item, month);

            specialParams.forEach((param, paramIdx) => {
                const mtdActual = param.getActualMtd(item);
                const mtdBudget = param.getBudgetMtd(item);
                const ytdActual = param.getActualYtd(item);
                const ytdBudget = param.getBudgetYtd(item);

                // Orange highlight: actual exists but no budget (only if hasBudget is true)
                const highlightMtdActualOrange =
                    param.hasBudget &&
                    mtdActual !== null &&
                    mtdActual > 0 &&
                    (mtdBudget === null || mtdBudget === 0);

                const highlightYtdActualOrange =
                    param.hasBudget &&
                    ytdActual !== null &&
                    ytdActual > 0 &&
                    (ytdBudget === null || ytdBudget === 0);

                rows.push({
                    block: item.block,
                    coa,
                    coaGroup: coa_group,
                    activity,
                    parameter: param.parameter,
                    rowType: `special_${paramIdx}`,
                    uom,
                    costActivityId: cost_activity_id,
                    mtdActual,
                    mtdBudget,
                    mtdVariance: mtdActual !== null && mtdBudget !== null
                        ? calculateVariance(mtdActual, mtdBudget)
                        : null,
                    ytdActual,
                    ytdBudget,
                    ytdVariance: ytdActual !== null && ytdBudget !== null
                        ? calculateVariance(ytdActual, ytdBudget)
                        : null,
                    highlightMtdActualOrange,
                    highlightYtdActualOrange,
                    highlightMtdActualYellow: false,
                    highlightYtdActualYellow: false,
                    isFirstRow: paramIdx === 0,
                    activityIndex: index,
                });
            });
        } else {
            // Standard handling (like upkeep monitoring)
            // Row 1: UOM (dynamic parameter from uom field)
            const mtdActualUom = item.work_act_mtd;
            const mtdBudgetUom = item.work_bgt_mtd;
            const ytdActualUom = item.work_act_ytd;
            const ytdBudgetUom = item.work_bgt_ytd;

            // Orange highlight for UOM row: actual exists but no budget
            const highlightMtdActualOrange = mtdActualUom > 0 && mtdBudgetUom === 0;
            const highlightYtdActualOrange = ytdActualUom > 0 && ytdBudgetUom === 0;

            rows.push({
                block: item.block,
                coa,
                coaGroup: coa_group,
                activity,
                parameter: uom,
                rowType: 'uom',
                uom,
                costActivityId: cost_activity_id,
                mtdActual: mtdActualUom,
                mtdBudget: mtdBudgetUom,
                mtdVariance: calculateVariance(mtdActualUom, mtdBudgetUom),
                ytdActual: ytdActualUom,
                ytdBudget: ytdBudgetUom,
                ytdVariance: calculateVariance(ytdActualUom, ytdBudgetUom),
                highlightMtdActualOrange,
                highlightYtdActualOrange,
                highlightMtdActualYellow: false,
                highlightYtdActualYellow: false,
                isFirstRow: true,
                activityIndex: index,
            });

            // Row 2: HK (static)
            const mtdActualHk = item.hk_act_mtd;
            const mtdBudgetHk = item.hk_bgt_mtd;
            const ytdActualHk = item.hk_act_ytd;
            const ytdBudgetHk = item.hk_bgt_ytd;

            rows.push({
                block: item.block,
                coa,
                coaGroup: coa_group,
                activity,
                parameter: 'HK',
                rowType: 'hk',
                uom,
                costActivityId: cost_activity_id,
                mtdActual: mtdActualHk,
                mtdBudget: mtdBudgetHk,
                mtdVariance: calculateVariance(mtdActualHk, mtdBudgetHk),
                ytdActual: ytdActualHk,
                ytdBudget: ytdBudgetHk,
                ytdVariance: calculateVariance(ytdActualHk, ytdBudgetHk),
                highlightMtdActualOrange: false,
                highlightYtdActualOrange: false,
                highlightMtdActualYellow: false,
                highlightYtdActualYellow: false,
                isFirstRow: false,
                activityIndex: index,
            });

            // Row 3: HK/UOM (calculated)
            const mtdActualRatio = safeDivide(item.hk_act_mtd, item.work_act_mtd);
            const mtdBudgetRatio = safeDivide(item.hk_bgt_mtd, item.work_bgt_mtd);
            const ytdActualRatio = safeDivide(item.hk_act_ytd, item.work_act_ytd);
            const ytdBudgetRatio = safeDivide(item.hk_bgt_ytd, item.work_bgt_ytd);

            // Yellow highlight for HK/UOM row: actual exceeds budget
            const highlightMtdActualYellow =
                mtdActualRatio !== null &&
                mtdBudgetRatio !== null &&
                mtdBudgetRatio > 0 &&
                mtdActualRatio > mtdBudgetRatio;

            const highlightYtdActualYellow =
                ytdActualRatio !== null &&
                ytdBudgetRatio !== null &&
                ytdBudgetRatio > 0 &&
                ytdActualRatio > ytdBudgetRatio;

            rows.push({
                block: item.block,
                coa,
                coaGroup: coa_group,
                activity,
                parameter: `HK/${uom}`,
                rowType: 'hk_per_uom',
                uom,
                costActivityId: cost_activity_id,
                mtdActual: mtdActualRatio,
                mtdBudget: mtdBudgetRatio,
                mtdVariance: mtdActualRatio !== null && mtdBudgetRatio !== null
                    ? calculateVariance(mtdActualRatio, mtdBudgetRatio)
                    : null,
                ytdActual: ytdActualRatio,
                ytdBudget: ytdBudgetRatio,
                ytdVariance: ytdActualRatio !== null && ytdBudgetRatio !== null
                    ? calculateVariance(ytdActualRatio, ytdBudgetRatio)
                    : null,
                highlightMtdActualOrange: false,
                highlightYtdActualOrange: false,
                highlightMtdActualYellow,
                highlightYtdActualYellow,
                isFirstRow: false,
                activityIndex: index,
            });
        }
    });

    const isValEmpty = (val: number | null | undefined) => val === 0 || val === null || val === undefined || Number.isNaN(val);
    return rows.filter(
        row => !(isValEmpty(row.mtdActual) && isValEmpty(row.mtdBudget) && isValEmpty(row.ytdActual) && isValEmpty(row.ytdBudget))
    );
};

/**
 * Transform rows into 3-level hierarchy: COA Group → COA → Activity
 */
export const transformToHierarchy = (rows: ProductionBudgetRow[]): ProductionBudgetHierarchy[] => {
    const hierarchyMap: Map<string, ProductionBudgetHierarchy> = new Map();

    rows.forEach((row) => {
        const { coaGroup, coa, activity } = row;

        // Level 1: COA Group
        if (!hierarchyMap.has(coaGroup)) {
            hierarchyMap.set(coaGroup, {
                coaGroup,
                coaList: [],
            });
        }

        const coaGroupData = hierarchyMap.get(coaGroup)!;

        // Level 2: COA
        let coaData = coaGroupData.coaList.find(c => c.coa === coa);
        if (!coaData) {
            coaData = { coa, activities: [] };
            coaGroupData.coaList.push(coaData);
        }

        // Level 3: Activity
        let activityData = coaData.activities.find(a => a.activity === activity);
        if (!activityData) {
            activityData = { activity, rows: [] };
            coaData.activities.push(activityData);
        }

        activityData.rows.push(row);
    });

    return Array.from(hierarchyMap.values());
};

/**
 * Group rows by COA for display (legacy support)
 */
export const groupRowsByCoa = (rows: ProductionBudgetRow[]): ProductionBudgetGrouped[] => {
    const grouped: Map<string, ProductionBudgetGrouped> = new Map();

    rows.forEach((row) => {
        if (!grouped.has(row.coa)) {
            grouped.set(row.coa, {
                coa: row.coa,
                coaGroup: row.coaGroup,
                activities: [],
            });
        }

        const coaGroupData = grouped.get(row.coa)!;
        let activityGroup = coaGroupData.activities.find(
            (a: { activity: string; rows: ProductionBudgetRow[] }) => a.activity === row.activity
        );

        if (!activityGroup) {
            activityGroup = { activity: row.activity, rows: [] };
            coaGroupData.activities.push(activityGroup);
        }

        activityGroup.rows.push(row);
    });

    return Array.from(grouped.values());
};

/**
 * Get month name in Indonesian
 */
export const getMonthName = (month: string): string => {
    const monthNames = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
    ];
    const monthIndex = Number.parseInt(month, 10) - 1;
    return monthNames[monthIndex] || '';
};

/**
 * Get month options for filter
 */
export const getMonthOptions = () => {
    return [
        { value: '01', label: 'Januari' },
        { value: '02', label: 'Februari' },
        { value: '03', label: 'Maret' },
        { value: '04', label: 'April' },
        { value: '05', label: 'Mei' },
        { value: '06', label: 'Juni' },
        { value: '07', label: 'Juli' },
        { value: '08', label: 'Agustus' },
        { value: '09', label: 'September' },
        { value: '10', label: 'Oktober' },
        { value: '11', label: 'November' },
        { value: '12', label: 'Desember' },
    ];
};

/**
 * Get year options for filter (last 5 years + current + next)
 */
export const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = currentYear + 1; i >= currentYear - 5; i--) {
        years.push({ value: i.toString(), label: i.toString() });
    }

    return years;
};
